"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import React, { useCallback, useEffect, useState } from "react"

import { QuoteType } from "@/types/quote"

import Button from "../Button"
import PageGrid from "../PageGrid"

type QuoteCarouselProps = {
  quoteData: QuoteType[]
}

const QuoteCarousel: React.FC<QuoteCarouselProps> = ({ quoteData }) => {
  // Move all hooks to the top of the component
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    containScroll: "trimSnaps",
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onDotClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi],
  )

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return undefined
    onSelect()
    emblaApi.on("select", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
    }
  }, [emblaApi, onSelect])

  // Safety check: return null if no quote data (after hooks)
  if (!quoteData || quoteData.length === 0) {
    return null
  }

  return (
    <section className="py-10 xl:py-28 lg:py-16 md:py-14 bg-gray-0">
      <div className="embla overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex">
          {quoteData.map((slide: QuoteType) => (
            <div
              key={slide.name}
              className="embla__slide flex-[0_0_100%] min-w-0"
            >
              <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
                <div className="p-4 mb-8 md:mb-10 lg:mb-0 lg:col-span-4 col-span-full">
                  <Image
                    src={slide?.companyImage?.url}
                    alt={slide?.title}
                    width={slide?.companyImage?.width}
                    height={slide?.companyImage?.height}
                    className="xl:h-[52px] lg:h-[46px] h-[40px] object-contain object-left lg:object-center"
                  />
                </div>
                <div className="p-4 lg:col-span-8 col-span-full">
                  <Image
                    sizes="(min-width: 56px) 56px, 100vw"
                    width={23}
                    height={23}
                    src="/homepage/quote-marks@2x.png"
                    alt="Decorative Quotation Marks"
                    className="h-[23px] w-[23px] mb-8 object-contain object-center"
                  />
                  <p className="mb-8 body1 text-gray-90 xl:mb-12 md:mb-10">
                    {slide.quote}
                  </p>
                  <div className="flex items-center">
                    {slide?.quoteeImage?.url && (
                      <Image
                        src={slide?.quoteeImage?.url}
                        alt={slide?.title}
                        width={200}
                        height={200}
                        className="xl:h-[36px] lg:h-[28px] h-[24px] object-contain object-left lg:object-center"
                      />
                    )}

                    <div>
                      <p className="uppercase caption2 text-gray-90">
                        {slide?.name}
                        <br />
                        {slide?.title}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <PageGrid>
        <div className="p-4 col-span-full lg:col-start-5 lg:col-span-8">
          <div className="flex items-center mb-10 -ml-2">
            {quoteData.map((slide, index) => (
              <button
                key={slide.name}
                onClick={() => onDotClick(index)}
                className="p-4"
                type="button"
                aria-label={`Go to slide ${index + 1}`}
              >
                <div
                  className={clsx(
                    "rounded-full mr-8",
                    selectedIndex === index
                      ? "rounded-full bg-blue-50 w-3 h-3"
                      : "w-2 h-2 bg-gray-60",
                  )}
                />
              </button>
            ))}
          </div>
          <Button to="/customer-case-study" theme="light" variant="tertiary">
            Hear from more customers
          </Button>
        </div>
      </PageGrid>
    </section>
  )
}

export default QuoteCarousel
